import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { KeyRound, Check, X, ShieldAlert, Eye, EyeOff } from "lucide-react";
import AppShell from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

/**
 * Iter75.2 — Standalone Change-Password page.
 *
 * Used by two flows:
 *   1. `/change-password?reason=rotation` — triggered by a login that
 *      hits HTTP 428 (password older than 90 days for a privileged
 *      role). The user is authenticated on the backend but the SPA has
 *      no session cookie yet, so we surface a specific banner and gate
 *      the form on the "current password" they just typed.
 *   2. Direct link from `/account` for a routine change.
 *
 * The 12-char-min policy comes from `/api/auth/password-rules`. If the
 * server rules list is unavailable we fall back to the Iter75 policy.
 */
const TIER1_RULES = [
    { key: "min_length_12", label: "At least 12 characters", test: (p) => p.length >= 12 },
    { key: "uppercase", label: "At least one uppercase letter (A–Z)", test: (p) => /[A-Z]/.test(p) },
    { key: "lowercase", label: "At least one lowercase letter (a–z)", test: (p) => /[a-z]/.test(p) },
    { key: "digit", label: "At least one digit (0–9)", test: (p) => /\d/.test(p) },
    { key: "symbol", label: "At least one symbol (!@#$…)", test: (p) => /[^A-Za-z0-9]/.test(p) },
];

function scorePassword(pw) {
    if (!pw) return { level: 0, label: "Empty", color: "#9CA3AF" };
    const passed = TIER1_RULES.filter((r) => r.test(pw)).length;
    if (passed <= 2) return { level: 1, label: "Weak", color: "#DC2626" };
    if (passed === 3) return { level: 2, label: "Fair", color: "#D97706" };
    if (passed === 4) return { level: 3, label: "Good", color: "#059669" };
    return { level: 4, label: "Strong", color: "#047857" };
}

function PasswordField({ id, label, value, onChange, testid, autoComplete }) {
    const [show, setShow] = useState(false);
    return (
        <div className="space-y-2">
            <Label htmlFor={id}>{label}</Label>
            <div className="relative">
                <Input
                    id={id}
                    type={show ? "text" : "password"}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    autoComplete={autoComplete}
                    className="pr-10"
                    data-testid={testid}
                />
                <button
                    type="button"
                    onClick={() => setShow((v) => !v)}
                    aria-label={show ? "Hide password" : "Show password"}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-[color:var(--asl-muted)] hover:asl-navy-text"
                    data-testid={`${testid}-toggle`}
                >
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
            </div>
        </div>
    );
}

export default function ChangePassword() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { user, refresh, logout } = useAuth();
    const forced = params.get("reason") === "rotation";
    const [cur, setCur] = useState("");
    const [n1, setN1] = useState("");
    const [n2, setN2] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    // Guard: rotation redirect can happen before the SPA has an auth
    // cookie. In that case, `user` will be false — redirect to /login.
    useEffect(() => {
        if (!forced && user === false) navigate("/login", { replace: true });
    }, [forced, user, navigate]);

    const rules = useMemo(() => TIER1_RULES.map((r) => ({ ...r, ok: r.test(n1) })), [n1]);
    const allRulesPass = rules.every((r) => r.ok);
    const passwordsMatch = n1.length > 0 && n1 === n2;
    const notSameAsCurrent = n1.length === 0 || n1 !== cur;
    const canSubmit = cur.length > 0 && allRulesPass && passwordsMatch && notSameAsCurrent && !busy;
    const strength = scorePassword(n1);

    const submit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setBusy(true);
        setError("");
        try {
            // For a rotation-forced flow we still need to authenticate the
            // change — the backend requires the current password.
            await api.post("/auth/change-password", {
                current_password: cur,
                new_password: n1,
                confirm_password: n2,
            });
            toast.success("Password updated.");
            if (forced) {
                // Force a re-login so the fresh session cookie is minted
                // with the new password_changed_at anchor.
                try { await logout(); } catch (_e) { /* ignore */ }
                navigate("/login?reason=password_updated", { replace: true });
            } else {
                setCur(""); setN1(""); setN2("");
                refresh();
            }
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const body = (
        <section className="max-w-xl mx-auto space-y-6" data-testid="change-password-page">
            <header className="flex items-start gap-3">
                <KeyRound className="h-7 w-7 asl-navy-text" />
                <div>
                    <h2 className="text-xl font-bold asl-navy-text">Change your password</h2>
                    <p className="text-sm text-[color:var(--asl-muted)] mt-1">
                        Passwords rotate every 90 days for privileged roles.
                    </p>
                </div>
            </header>

            {forced && (
                <div
                    className="rounded-md border px-4 py-3 flex items-start gap-3"
                    style={{ background: "#FFF7E6", borderColor: "#F5C87B", color: "#7A4B00" }}
                    data-testid="change-password-rotation-banner"
                >
                    <ShieldAlert className="h-5 w-5 shrink-0 mt-0.5" />
                    <div className="text-sm">
                        <div className="font-semibold">Your password has expired.</div>
                        <div>Please set a new one to continue signing in.</div>
                    </div>
                </div>
            )}

            <form onSubmit={submit} className="asl-card p-6 space-y-4" data-testid="change-password-form">
                <PasswordField id="cp-current" label="Current password" value={cur} onChange={setCur}
                    autoComplete="current-password" testid="cp-current" />
                <PasswordField id="cp-new" label="New password" value={n1} onChange={setN1}
                    autoComplete="new-password" testid="cp-new" />
                <PasswordField id="cp-confirm" label="Confirm new password" value={n2} onChange={setN2}
                    autoComplete="new-password" testid="cp-confirm" />

                {n1 && (
                    <div className="space-y-1.5" data-testid="cp-strength">
                        <div className="flex items-center justify-between text-xs">
                            <span className="text-[color:var(--asl-muted)]">Password strength</span>
                            <span style={{ color: strength.color, fontWeight: 600 }}
                                data-testid="cp-strength-label">
                                {strength.label}
                            </span>
                        </div>
                        <div className="h-1.5 rounded-full overflow-hidden"
                            style={{ background: "var(--asl-bg,#F5F6F8)" }}>
                            <div
                                style={{
                                    height: "100%",
                                    width: `${(strength.level / 4) * 100}%`,
                                    background: strength.color,
                                    transition: "width 200ms ease",
                                }}
                                data-testid="cp-strength-bar"
                            />
                        </div>
                    </div>
                )}

                <ul className="rounded-md p-3 space-y-1 text-xs"
                    style={{ background: "var(--asl-bg,#F5F6F8)" }}
                    data-testid="cp-rules">
                    {rules.map((r) => (
                        <li key={r.key}
                            className={`flex items-center gap-2 ${r.ok ? "text-emerald-700" : "text-[color:var(--asl-muted)]"}`}
                            data-testid={`cp-rule-${r.key}-${r.ok ? "ok" : "fail"}`}>
                            {r.ok ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                            <span>{r.label}</span>
                        </li>
                    ))}
                    <li className={`flex items-center gap-2 ${passwordsMatch ? "text-emerald-700" : "text-[color:var(--asl-muted)]"}`}
                        data-testid={`cp-rule-match-${passwordsMatch ? "ok" : "fail"}`}>
                        {passwordsMatch ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                        <span>Confirmation matches the new password</span>
                    </li>
                    <li className={`flex items-center gap-2 ${notSameAsCurrent ? "text-emerald-700" : "text-rose-700"}`}
                        data-testid={`cp-rule-different-${notSameAsCurrent ? "ok" : "fail"}`}>
                        {notSameAsCurrent ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                        <span>New password differs from current</span>
                    </li>
                </ul>

                {error && (
                    <div className="rounded-md border px-3 py-2 text-sm"
                        style={{ background: "#FEF2F2", borderColor: "#FCA5A5", color: "#7F1D1D" }}
                        data-testid="cp-error">
                        {error}
                    </div>
                )}

                <Button
                    type="submit"
                    disabled={!canSubmit}
                    className="asl-navy-bg text-white"
                    data-testid="cp-submit"
                >
                    {busy ? "Updating…" : "Update password"}
                </Button>
            </form>
        </section>
    );

    // Forced flow renders bare (no AppShell) because the user may not
    // have a valid session yet when their password just expired.
    return forced ? <div className="min-h-screen py-10 px-4 bg-[color:var(--asl-bg,#F5F6F8)]">{body}</div>
        : <AppShell title="Change Password">{body}</AppShell>;
}
